import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import './LoginPage.css';
import Logo from '../../assets/logo-green.svg';
import GoogleIcon from '../../assets/google-icon.svg';
import AppleIcon from '../../assets/apple-icon.svg';
import { auth } from '../../firebase';
import { isValidEmail } from '../../services/validations/emailValidation';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar'; // Importar o hook

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const db = getFirestore();

  const { initClient } = useGoogleCalendar(); // Usar o hook do Google Calendar

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isValidEmail(email)) {
      setError('Por favor, insira um email válido.');
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setSuccess('Login realizado com sucesso! Redirecionando...');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      if (err.code === 'auth/wrong-password') {
        setError('Senha incorreta. Por favor, tente novamente.');
      } else if (err.code === 'auth/user-not-found') {
        setError(
          'Usuário não encontrado. Verifique seu email ou crie uma conta.'
        );
      } else if (err.code === 'auth/too-many-requests') {
        setError(
          'Muitas tentativas de login. Por favor, tente novamente mais tarde.'
        );
      } else {
        setError('Falha no login. Verifique suas credenciais.');
      }
    }
  };

  const handleSocialLogin = async (provider) => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential && credential.accessToken;

      // Verifica se o usuário já existe na coleção 'users'
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        const userData = {
          uid: user.uid,
          nome: user.displayName || 'Usuário sem nome',
          email: user.email,
          plano: 'Gratuito',
          createdAt: new Date().toISOString(),
          status: 'ativo',
          photoURL: user.photoURL || '',
        };
        await setDoc(userDocRef, userData);
      }

      initClient(accessToken); // Inicializa o Google Calendar logo após o login
      navigate('/');
    } catch (err) {
      console.error(err);
      setError(
        `Erro ao fazer login com ${provider.providerId}. Tente novamente.`
      );
    }
  };

  const handleGoogleLogin = () => {
    const provider = new GoogleAuthProvider();
    handleSocialLogin(provider);
  };

  const handleAppleLogin = () => {
    const provider = new OAuthProvider('apple.com');
    handleSocialLogin(provider);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="logo-container">
          <img src={Logo} alt="Plantão Fácil Logo" className="login-logo" />
        </div>
        <h1 className="login-title">Plantão Fácil</h1>
        <p className="login-subtitle">
          Acesse sua conta para gerenciar seus plantões
        </p>
        {error && (
          <p className="error-message" aria-live="polite">
            {error}
          </p>
        )}
        {success && (
          <p className="success-message" aria-live="polite">
            {success}
          </p>
        )}
        <form
          onSubmit={handleSubmit}
          className="login-form"
          aria-labelledby="login-title"
        >
          <div className="input-group">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Digite seu email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-required="true"
            />
          </div>
          <div className="input-group">
            <label htmlFor="password">Senha:</label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="Digite sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-required="true"
            />
          </div>
          <button type="submit" className="login-button">
            Entrar
          </button>
        </form>

        <div className="social-login-container">
          <button
            className="social-login-button google"
            onClick={handleGoogleLogin}
          >
            <img src={GoogleIcon} alt="Google Icon" className="social-icon" />
            Continuar com Google
          </button>
          <button
            className="social-login-button apple"
            onClick={handleAppleLogin}
          >
            <img src={AppleIcon} alt="Apple Icon" className="social-icon" />
            Continuar com Apple
          </button>
        </div>

        <div className="login-footer">
          <a href="/recuperar-senha" className="login-link">
            Esqueceu sua senha?
          </a>
          <a href="/cadastrar" className="login-link">
            Criar uma conta
          </a>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
